// Platform Admin — 📊 Video Performance Analytics
// =================================================
// Fetches /api/veo/analytics/dashboard and renders KPI tiles, template
// performance, top schools, daily trend, and recent publishes.
import React, { useEffect, useState } from 'react';
import BestHourHeatmap from './BestHourHeatmap';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATE_LABELS = {
  welcome_tour: '🏫 Welcome Tour',
  celebration: '🎉 Classroom',
  achievement: '🏆 Achievement',
  principal_vision: '👨‍🏫 Principal',
  custom: '✍️ Custom',
};

function MiniBar({ values, max, color = 'bg-purple-500' }) {
  const safeMax = Math.max(max || 0, 1);
  return (
    <div className="flex items-end gap-1 h-16">
      {values.map((v, i) => (
        <div key={i} className={`${color} rounded-t flex-1 transition-all`} style={{ height: `${Math.max((v / safeMax) * 100, 4)}%` }} title={`${v}`} />
      ))}
    </div>
  );
}

export default function VideoAnalyticsTab({ token, language = 'hi' }) {
  const [data, setData] = useState(null);
  const [recos, setRecos] = useState(null);
  const [needsRetry, setNeedsRetry] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('30d');
  const [refreshing, setRefreshing] = useState(null); // publish_id being refreshed

  const load = async (r = range) => {
    setLoading(true);
    try {
      const [dRes, recoRes, retryRes, jobsRes] = await Promise.all([
        fetch(`${API_URL}/api/veo/analytics/dashboard?range=${r}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/veo/analytics/recommendations?range=${r}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/veo/publishes/needs-retry?min_age_hours=24`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/veo/jobs?status=completed&limit=50`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const d = await dRes.json();
      const reco = await recoRes.json();
      const retry = await retryRes.json();
      const jobs = await jobsRes.json();
      if (d.success) setData(d);
      if (reco.success) setRecos(reco);
      if (retry.success) setNeedsRetry(retry.items || []);
      if (jobs.success) setCompletedJobs((jobs.items || []).filter((j) => j.status === 'completed'));
    } catch (e) { console.warn('analytics:', e); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const refreshOne = async (pid) => {
    setRefreshing(pid);
    try {
      await fetch(`${API_URL}/api/veo/analytics/refresh/${pid}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      await load();
    } catch (e) { console.warn('refresh:', e); }
    setRefreshing(null);
  };

  if (loading && !data) return <div className="p-6 text-gray-500">Loading analytics…</div>;
  if (!data) return <div className="p-6 text-red-600">Failed to load analytics</div>;

  const { tiles, templates, top_schools, daily_trend, recent_publishes } = data;
  const trendValues = daily_trend.map((d) => d.count);
  const trendMax = Math.max(...trendValues, 1);

  return (
    <div className="space-y-4" data-testid="video-analytics-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-xl font-bold text-gray-800" style={{ fontFamily: 'Outfit' }}>
            📊 {language === 'hi' ? 'Video Performance Analytics' : 'Video Performance Analytics'}
          </h3>
          <p className="text-xs text-gray-500">
            {language === 'hi' ? 'Publishes, engagement और template-level performance trends' : 'Publishes, engagement, and template-level performance'}
          </p>
        </div>
        <div className="inline-flex bg-slate-100 rounded-lg p-1 gap-1" data-testid="analytics-range-tabs">
          {['7d', '30d', '90d', 'all'].map((r) => (
            <button
              key={r}
              onClick={() => { setRange(r); load(r); }}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition ${range === r ? 'bg-white shadow text-purple-700' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid={`range-${r}`}
            >
              {r.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Auto-Republish suggestions banner */}
      {needsRetry.length > 0 && (
        <div className="bg-gradient-to-r from-orange-100 to-red-50 border border-orange-300 rounded-2xl p-4" data-testid="needs-retry-banner">
          <div className="flex items-start gap-3">
            <span className="text-2xl">🔄</span>
            <div className="flex-1">
              <p className="font-bold text-orange-900 text-sm">
                {language === 'hi'
                  ? `${needsRetry.length} video${needsRetry.length === 1 ? '' : 's'} को कोई engagement नहीं मिला`
                  : `${needsRetry.length} video${needsRetry.length === 1 ? '' : 's'} got zero engagement`}
              </p>
              <p className="text-xs text-orange-800 mt-0.5">
                {language === 'hi' ? 'इन्हें "Best Hour" पर republish करें ज़्यादा reach के लिए।' : 'Re-publish at "Best Hour" for higher reach.'}
              </p>
              <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {needsRetry.slice(0, 5).map((it) => (
                  <div key={it.publish_id} className="text-[11px] flex items-center justify-between bg-white/70 rounded px-2 py-1" data-testid={`retry-item-${it.publish_id}`}>
                    <span><strong>{it.school_name}</strong> · {it.channels.join(',')} · {it.age_hours}h ago</span>
                    <span className="text-orange-700">{it.publish_id.slice(-8)}</span>
                  </div>
                ))}
                {needsRetry.length > 5 && <p className="text-[10px] text-gray-500">+ {needsRetry.length - 5} more</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KPI Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-4 text-white" data-testid="tile-publishes">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-white/80">Total Publishes</p>
          <p className="text-3xl font-black mt-1" style={{ fontFamily: 'Outfit' }}>{tiles.total_publishes}</p>
          <p className="text-[11px] text-white/70 mt-1">{language === 'hi' ? 'सभी channels combined' : 'All channels combined'}</p>
        </div>
        <div className="bg-gradient-to-br from-pink-500 to-rose-500 rounded-2xl p-4 text-white" data-testid="tile-instagram">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-white/80">📷 Instagram</p>
          <p className="text-2xl font-black mt-1" style={{ fontFamily: 'Outfit' }}>{tiles.instagram.plays.toLocaleString('en-IN')} plays</p>
          <p className="text-[11px] text-white/80 mt-1">
            ❤️ {tiles.instagram.likes} · 💬 {tiles.instagram.comments} · 👀 {tiles.instagram.reach}
          </p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-4 text-white" data-testid="tile-whatsapp">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-white/80">💬 WhatsApp</p>
          <p className="text-2xl font-black mt-1" style={{ fontFamily: 'Outfit' }}>{tiles.whatsapp.delivered}/{tiles.whatsapp.total} delivered</p>
          <p className="text-[11px] text-white/80 mt-1">
            👁 read: {tiles.whatsapp.read} · ❌ failed: {tiles.whatsapp.failed}
          </p>
        </div>
      </div>

      {/* Smart Recommendations */}
      {recos && recos.sample_size > 0 && (
        <div className="bg-gradient-to-r from-amber-100 to-yellow-50 border border-amber-300 rounded-2xl p-4" data-testid="smart-recommendations">
          <h4 className="font-bold text-amber-900 text-sm mb-2 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            🤖 {language === 'hi' ? 'Smart Insights' : 'Smart Insights'}
            {recos.note && <span className="text-[10px] font-normal bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">{recos.note}</span>}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {recos.top_template && (
              <div className="bg-white rounded-xl p-3 border border-amber-200" data-testid="reco-top-template">
                <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider mb-1">🔥 Top Performer Template</p>
                <p className="text-base font-black">
                  {TEMPLATE_LABELS[recos.top_template.template_id] || recos.top_template.template_id}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {language === 'hi'
                    ? `Avg engagement ${recos.top_template.avg_engagement_score} (${recos.top_template.lift_pct_vs_avg >= 0 ? '+' : ''}${recos.top_template.lift_pct_vs_avg}% vs platform avg)`
                    : `Avg engagement ${recos.top_template.avg_engagement_score} (${recos.top_template.lift_pct_vs_avg >= 0 ? '+' : ''}${recos.top_template.lift_pct_vs_avg}% vs platform avg)`}
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">Sample: {recos.top_template.sample_size} videos</p>
              </div>
            )}
            <div className="bg-white rounded-xl p-3 border border-amber-200" data-testid="reco-best-hour">
              <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider mb-1">⏰ Best Publish Hour</p>
              {recos.best_publish_hour ? (
                <>
                  <p className="text-base font-black">{recos.best_publish_hour.label}</p>
                  <p className="text-xs text-gray-600 mt-1">
                    {recos.best_publish_hour.share_pct}% {language === 'hi' ? 'reads इसी window में' : 'of reads in this window'}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5">Sample: {recos.best_publish_hour.sample_size} reads</p>
                </>
              ) : (
                <p className="text-xs text-gray-500">
                  {language === 'hi'
                    ? 'और data चाहिए — कम से कम कुछ delivered + read events।'
                    : 'Need more data — at least a few delivered + read events.'}
                </p>
              )}
            </div>
          </div>
          {recos.school_tips && recos.school_tips.length > 0 && (
            <div className="mt-3" data-testid="reco-school-tips">
              <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider mb-1">📩 {language === 'hi' ? 'Schools के लिए Personalized Tips' : 'Personalized School Tips'}</p>
              <div className="space-y-1">
                {recos.school_tips.slice(0, 5).map((t) => (
                  <div key={t.last_publish_id} className="bg-white/60 rounded-lg px-3 py-2 text-xs" data-testid={`tip-${t.last_publish_id}`}>
                    <span className="font-semibold">{t.school_name}:</span>{' '}
                    {language === 'hi' ? t.message_hi : t.message_en}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Best-Hour Heatmap */}
      <BestHourHeatmap token={token} language={language} completedJobs={completedJobs} />

      {/* Daily Trend */}
      <div className="bg-white rounded-xl p-4 border" data-testid="daily-trend">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-gray-800 text-sm">{language === 'hi' ? 'Daily Publish Trend' : 'Daily Publish Trend'}</h4>
          <span className="text-[10px] text-gray-500">{daily_trend.length} {language === 'hi' ? 'दिन' : 'days'}</span>
        </div>
        {daily_trend.length === 0 ? (
          <p className="text-xs text-gray-500">No data in this range</p>
        ) : (
          <>
            <MiniBar values={trendValues} max={trendMax} color="bg-purple-500" />
            <div className="flex justify-between text-[10px] text-gray-400 mt-1">
              <span>{daily_trend[0]?.date}</span>
              <span>peak: {trendMax}</span>
              <span>{daily_trend[daily_trend.length - 1]?.date}</span>
            </div>
          </>
        )}
      </div>

      {/* Template Performance + Top Schools */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl p-4 border" data-testid="template-performance">
          <h4 className="font-semibold text-gray-800 text-sm mb-3">🎬 {language === 'hi' ? 'Template Performance' : 'Template Performance'}</h4>
          {templates.length === 0 ? (
            <p className="text-xs text-gray-500">No publishes yet</p>
          ) : (
            <div className="space-y-2">
              {templates.map((t) => {
                const score = t.ig_plays + t.ig_likes * 5 + t.wa_delivered * 2;
                const max = Math.max(...templates.map((x) => x.ig_plays + x.ig_likes * 5 + x.wa_delivered * 2), 1);
                const pct = (score / max) * 100;
                return (
                  <div key={t.template_id} className="text-xs" data-testid={`template-row-${t.template_id}`}>
                    <div className="flex justify-between mb-0.5">
                      <span className="font-semibold">{TEMPLATE_LABELS[t.template_id] || t.template_id}</span>
                      <span className="text-gray-500">{t.count} {language === 'hi' ? 'video' : 'videos'} · {t.ig_plays} plays · {t.wa_delivered} WA</span>
                    </div>
                    <div className="bg-gray-100 rounded h-2">
                      <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded h-2" style={{ width: `${Math.max(pct, 4)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl p-4 border" data-testid="top-schools">
          <h4 className="font-semibold text-gray-800 text-sm mb-3">🏫 {language === 'hi' ? 'Top Schools by Publishes' : 'Top Schools by Publishes'}</h4>
          {top_schools.length === 0 ? (
            <p className="text-xs text-gray-500">{language === 'hi' ? 'अभी कोई school का record नहीं — Showcase जब school-admin से publish हो तब आएगा' : 'No school-scoped publishes yet (will appear when school admins publish)'}</p>
          ) : (
            <div className="space-y-1.5">
              {top_schools.map((s, i) => (
                <div key={s.school_id} className="flex items-center justify-between text-xs" data-testid={`top-school-${i}`}>
                  <span className="truncate">{i + 1}. {s.school_name}</span>
                  <span className="font-bold text-purple-700">{s.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Publishes */}
      <div className="bg-white rounded-xl p-4 border" data-testid="recent-publishes">
        <h4 className="font-semibold text-gray-800 text-sm mb-3">⏱ {language === 'hi' ? 'हालिया Publishes' : 'Recent Publishes'}</h4>
        {recent_publishes.length === 0 ? (
          <p className="text-xs text-gray-500">No publishes yet</p>
        ) : (
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {recent_publishes.map((p) => (
              <div key={p.publish_id} className="border rounded-lg p-3 text-xs" data-testid={`recent-${p.publish_id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{p.school_name}</span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">{TEMPLATE_LABELS[p.template_id] || p.template_id}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${p.status === 'completed' ? 'bg-green-100 text-green-700' : p.status === 'partial_failure' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100'}`}>
                      {p.status}
                    </span>
                  </div>
                  <button
                    onClick={() => refreshOne(p.publish_id)}
                    disabled={refreshing === p.publish_id}
                    className="text-[10px] px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded disabled:opacity-50"
                    data-testid={`refresh-${p.publish_id}`}
                  >
                    {refreshing === p.publish_id ? '…' : '↻ Refresh'}
                  </button>
                </div>
                <p className="text-[10px] text-gray-400 mt-1">{new Date(p.created_at).toLocaleString()} · channels: {p.channels.join(', ')}</p>
                <div className="flex flex-wrap gap-3 mt-2">
                  {p.channels.includes('instagram') && (
                    <div className="text-[11px]">
                      📷 {p.instagram.plays} plays · ❤️ {p.instagram.likes} · 💬 {p.instagram.comments}
                      {p.instagram.permalink && <a href={p.instagram.permalink} target="_blank" rel="noreferrer" className="ml-1 text-blue-600 underline">view</a>}
                    </div>
                  )}
                  {p.channels.includes('whatsapp') && (
                    <div className="text-[11px]">
                      💬 {p.whatsapp.delivered}/{p.whatsapp.total} delivered · 👁 {p.whatsapp.read} read
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-[10px] text-gray-400 italic">
        {language === 'hi'
          ? '⏱ WhatsApp delivery/read receipts Meta webhook से auto-update होते हैं। Instagram metrics Refresh button से fetch होते हैं।'
          : '⏱ WhatsApp delivery/read receipts auto-update via Meta webhook. Instagram metrics fetched on Refresh.'}
      </p>
    </div>
  );
}
